/*
Copyright (C) 2023-2026 QuantumNous

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

For commercial licensing, please contact support@quantumnous.com
*/
import z from 'zod';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { UsageLogs } from '@/features/usage-logs';
import { isUsageLogsSectionId, USAGE_LOGS_DEFAULT_SECTION } from '@/features/usage-logs/section-registry';
const logTypeValues = ['0', '1', '2', '3', '4', '5', '6'] as const;
const usageLogsSearchSchema = z.object({
  page: z.number().optional().catch(1),
  pageSize: z.number().optional().catch(20),
  type: z.array(z.enum(logTypeValues)).optional().catch([]),
  filter: z.string().optional().catch(''),
  model: z.string().optional().catch(''),
  token: z.string().optional().catch(''),
  channel: z.string().optional().catch(''),
  group: z.string().optional().catch(''),
  username: z.string().optional().catch(''),
  requestId: z.string().optional().catch(''),
  upstreamRequestId: z.string().optional().catch(''),
  startTime: z.number().optional(),
  endTime: z.number().optional()
});
export const Route = createFileRoute('/_authenticated/usage-logs/$section')({
  beforeLoad: ({
    params,
    search
  }) => {
    if (!isUsageLogsSectionId(params.section)) {
      throw redirect({
        to: '/usage-logs/$section',
        params: {
          section: USAGE_LOGS_DEFAULT_SECTION
        }
      });
    }
    // type 仅 common 使用，非 common 时清掉 URL 里的 type
    if (params.section !== 'common' && Array.isArray(search?.type) && (search?.type?.length ?? 0) > 0) {
      throw redirect({
        to: '/usage-logs/$section',
        params: {
          section: params.section
        },
        search: {
          ...search,
          type: undefined
        },
        replace: true
      });
    }
  },
  validateSearch: usageLogsSearchSchema,
  component: UsageLogs
});
import { performReactRefresh as __tsr_performReactRefresh } from 'react-refresh/runtime';
if (import.meta.webpackHot) {
  const hot = import.meta.webpackHot;
  const hotData = hot.data ??= {};
  const routeId = hotData['tsr-route-id'] ?? Route.id ?? (Route.isRoot ? '__root__' : "/_authenticated/usage-logs/$section");
  if (routeId) {
    hotData['tsr-route-id'] = routeId;
  }
  const existingRoute = typeof window !== 'undefined' && routeId ? window.__TSR_ROUTER__?.routesById?.[routeId] : undefined;
  if (routeId && existingRoute && existingRoute !== Route) {
    (function handleRouteUpdate(routeId, newRoute) {
      const router = window.__TSR_ROUTER__;
      const oldRoute = router.routesById[routeId];
      if (!oldRoute) return;
      const removedKeys = new Set();
      Object.keys(oldRoute.options).forEach(key => {
        if (!(key in newRoute.options)) {
          removedKeys.add(key);
          delete oldRoute.options[key];
        }
      });
      const preserveComponentIdentity = "shellComponent" in oldRoute.options === "shellComponent" in newRoute.options;
      const componentKeys = ["component", "shellComponent", "pendingComponent", "errorComponent", "notFoundComponent"];
      if (preserveComponentIdentity) componentKeys.forEach(key => {
        if (key in oldRoute.options && key in newRoute.options) newRoute.options[key] = oldRoute.options[key];
      });
      oldRoute.options = newRoute.options;
      oldRoute.update(newRoute.options);
      oldRoute._componentsPromise = void 0;
      oldRoute._lazyPromise = void 0;
      router.routesById[oldRoute.id] = oldRoute;
      router.routesByPath[oldRoute.fullPath] = oldRoute;
      router.processedTree.matchCache.clear();
      router.processedTree.flatCache?.clear();
      router.processedTree.singleCache.clear();
      router.resolvePathCache.clear();
      walkReplaceSegmentTree(oldRoute, router.processedTree.segmentTree);
      const filter = m => m.routeId === oldRoute.id;
      const activeMatch = router.stores.matches.get().find(filter);
      const pendingMatch = router.stores.pendingMatches.get().find(filter);
      const cachedMatches = router.stores.cachedMatches.get().filter(filter);
      if (activeMatch || pendingMatch || cachedMatches.length > 0) {
        if (removedKeys.has("loader") || removedKeys.has("beforeLoad")) {
          const matchIds = [activeMatch?.id, pendingMatch?.id, ...cachedMatches.map(match => match.id)].filter(Boolean);
          router.batch(() => {
            for (const matchId of matchIds) {
              const store = router.stores.pendingMatchStores.get(matchId) || router.stores.matchStores.get(matchId) || router.stores.cachedMatchStores.get(matchId);
              if (store) store.set(prev => {
                const next = {
                  ...prev
                };
                if (removedKeys.has("loader")) next.loaderData = void 0;
                if (removedKeys.has("beforeLoad")) {
                  next.__beforeLoadContext = void 0;
                  next.context = rebuildMatchContextWithoutBeforeLoad(next);
                }
                return next;
              });
            }
          });
        }
        router.invalidate({
          filter,
          sync: true
        });
      }
      function walkReplaceSegmentTree(route, node) {
        if (node.route?.id === route.id) node.route = route;
        if (node.index) walkReplaceSegmentTree(route, node.index);
        node.static?.forEach(child => walkReplaceSegmentTree(route, child));
        node.staticInsensitive?.forEach(child => walkReplaceSegmentTree(route, child));
        node.dynamic?.forEach(child => walkReplaceSegmentTree(route, child));
        node.optional?.forEach(child => walkReplaceSegmentTree(route, child));
        node.wildcard?.forEach(child => walkReplaceSegmentTree(route, child));
      }
      function getStoreMatch(matchId) {
        return router.stores.pendingMatchStores.get(matchId)?.get() || router.stores.matchStores.get(matchId)?.get() || router.stores.cachedMatchStores.get(matchId)?.get();
      }
      function getMatchList(matchId) {
        const pendingMatches = router.stores.pendingMatches.get();
        if (pendingMatches.some(match => match.id === matchId)) return pendingMatches;
        const activeMatches = router.stores.matches.get();
        if (activeMatches.some(match => match.id === matchId)) return activeMatches;
        const cachedMatches = router.stores.cachedMatches.get();
        if (cachedMatches.some(match => match.id === matchId)) return cachedMatches;
        return [];
      }
      function getParentMatch(match) {
        const matchList = getMatchList(match.id);
        const matchIndex = matchList.findIndex(item => item.id === match.id);
        if (matchIndex <= 0) return;
        const parentMatch = matchList[matchIndex - 1];
        return getStoreMatch(parentMatch.id) || parentMatch;
      }
      function rebuildMatchContextWithoutBeforeLoad(match) {
        const parentMatch = getParentMatch(match);
        const getParentContext = router.getParentContext;
        return {
          ...((getParentContext ? getParentContext.call(router, parentMatch) : parentMatch?.context ?? router.options.context) ?? {}),
          ...(match.__routeContext ?? {})
        };
      }
    })(routeId, Route);
    const tsrRefreshState = globalThis.__TSR_HMR__ ??= {};
    try {
      if (!tsrRefreshState.refreshScheduled) {
        tsrRefreshState.refreshScheduled = true;
        setTimeout(() => {
          tsrRefreshState.refreshScheduled = false;
          try {
            __tsr_performReactRefresh();
          } catch (_e) {}
        }, 30);
      }
    } catch (_err) {}
  }
  hot.dispose(data => {
    if (routeId) {
      data['tsr-route-id'] = routeId;
    }
  });
  hot.accept();
}